<script>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import { ref, computed, onMounted, watch } from "vue";
import moment from "moment";
import DateRangePicker from "daterangepicker";

import { paymentReport as basePaymentReportConfig } from "./data";
import { usePaymentStore } from "@/stores/paymentStore";
import PaymentsReportTable from "./payment-report-table.vue";

export default {
  components: {
    PaymentsReportTable,
  },
  data() {
    return {
      title: "Payment Report",
      text: "HR",
      text1: "Payment Report",
    };
  },
  methods: {
    toggleHeader() {
      document.getElementById("collapse-header")?.classList.toggle("active");
      document.body.classList.toggle("header-collapse");
    },
  },
  setup() {
    const paymentStore = usePaymentStore();

    const loading = computed(() => paymentStore.loading);
    const meta = computed(() => paymentStore.meta);
    const items = computed(() => paymentStore.items);
    const summary = computed(() => paymentStore.summary);

    const dateRangeInput = ref(null);
    const sortLabel = ref("Last 7 Days");
    const methodLabel = ref("All Methods");

    // ---- Summary cards helpers ----
    function formatMoney(value) {
      const num = Number(value || 0);
      return num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    const totalPaymentsCount = computed(() => summary.value.count || 0);
    const totalPaymentsAmount = computed(() => summary.value.total_amount || 0);

    const pendingPaymentsAmount = computed(
      () => summary.value.pending_amount || 0
    );
    const failedPaymentsAmount = computed(
      () => summary.value.failed_amount || 0
    );

    // success rate based on counts
    const successRate = computed(() => {
      const total = summary.value.count || 0;
      const successCount = summary.value.success_count || 0;

      if (!total) return 0;
      return Math.round((successCount / total) * 100);
    });

    // ---- Donut chart (Payments by method) ----
    const chartOptions = ref({ ...basePaymentReportConfig.sline });
    const chartSeries = ref([]);

    function rebuildChart() {
      const byMethod = {};

      (items.value || []).forEach((p) => {
        const key = p.method || "Unknown";
        const amount = Number(p.amount || 0);
        byMethod[key] = (byMethod[key] || 0) + amount;
      });

      const labels = Object.keys(byMethod);
      const series = labels.map((l) => byMethod[l]);

      chartSeries.value = series;
      chartOptions.value = {
        ...basePaymentReportConfig.sline,
        labels,
      };
    }

    watch(items, rebuildChart, { immediate: true });

    // ---- Year dropdown for the donut chart ----
    const selectedYearLabel = ref("This Year");
    const yearOptions = ref([
      { label: "This Year", value: "this_year" },
      {
        label: String(moment().year()),
        value: moment().year(),
      },
      {
        label: String(moment().year() - 1),
        value: moment().year() - 1,
      },
      {
        label: String(moment().year() - 2),
        value: moment().year() - 2,
      },
    ]);

    function applyYearFilter(option) {
      selectedYearLabel.value = option.label;

      let fromMoment;
      let toMoment;

      if (option.value === "this_year") {
        const y = moment().year();
        fromMoment = moment().year(y).startOf("year");
        toMoment = moment().year(y).endOf("year");
      } else {
        const y = option.value;
        fromMoment = moment().year(y).startOf("year");
        toMoment = moment().year(y).endOf("year");
      }

      const from = fromMoment.format("YYYY-MM-DD");
      const to = toMoment.format("YYYY-MM-DD");

      paymentStore.filters.from = from;
      paymentStore.filters.to = to;

      if (dateRangeInput.value) {
        dateRangeInput.value.value =
          fromMoment.format("DD/MM/YYYY") +
          " - " +
          toMoment.format("DD/MM/YYYY");
      }

      applyFilters(1, { from, to });
    }

    // ---- Filters + fetch ----
    async function applyFilters(page = 1, extra = {}) {
      await paymentStore.fetchAll({
        page,
        from: extra.from ?? paymentStore.filters.from ?? undefined,
        to: extra.to ?? paymentStore.filters.to ?? undefined,
        q: extra.q ?? paymentStore.filters.q ?? undefined,
        method: extra.method ?? paymentStore.filters.method ?? undefined,
        sort: extra.sort ?? paymentStore.filters.sort ?? undefined,
      });
    }

    function handlePageChange(page) {
      applyFilters(page);
    }

    function setSort(sort, label) {
      paymentStore.filters.sort = sort;
      sortLabel.value = label;
      applyFilters(1, { sort });
    }

    function setMethod(method, label) {
      paymentStore.filters.method = method || "";
      methodLabel.value = label || "All Methods";
      applyFilters(1, { method: paymentStore.filters.method || undefined });
    }

    // ---- Date range picker ----
    onMounted(async () => {
      const start = moment().subtract(6, "days");
      const end = moment();

      paymentStore.filters.from = start.format("YYYY-MM-DD");
      paymentStore.filters.to = end.format("YYYY-MM-DD");

      if (dateRangeInput.value) {
        new DateRangePicker(
          dateRangeInput.value,
          {
            startDate: start,
            endDate: end,
            ranges: {
              Today: [moment(), moment()],
              Yesterday: [
                moment().subtract(1, "days"),
                moment().subtract(1, "days"),
              ],
              "Last 7 Days": [moment().subtract(6, "days"), moment()],
              "Last 30 Days": [moment().subtract(29, "days"), moment()],
              "This Month": [
                moment().startOf("month"),
                moment().endOf("month"),
              ],
              "Last Month": [
                moment().subtract(1, "month").startOf("month"),
                moment().subtract(1, "month").endOf("month"),
              ],
            },
          },
          function booking_range(start, end) {
            if (dateRangeInput.value) {
              dateRangeInput.value.value =
                start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
            }

            paymentStore.filters.from = start.format("YYYY-MM-DD");
            paymentStore.filters.to = end.format("YYYY-MM-DD");

            applyFilters(1, {
              from: paymentStore.filters.from,
              to: paymentStore.filters.to,
            });
          }
        );

        dateRangeInput.value.value =
          start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
      }

      await applyFilters(1);
    });

    // ---- Export helpers ----
    function downloadBlob(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }

    async function handleExportPdf() {
      try {
        const blob = await paymentStore.exportPdf();
        downloadBlob(blob, `invoice-payments-${Date.now()}.pdf`);
      } catch {
        // toast already handled in store
      }
    }

    async function handleExportExcel() {
      try {
        const blob = await paymentStore.exportExcel();
        downloadBlob(blob, `invoice-payments-${Date.now()}.xlsx`);
      } catch {
        // toast already handled in store
      }
    }

    return {
      // store refs
      paymentStore,
      loading,
      meta,
      items,
      summary,

      // UI state
      dateRangeInput,
      sortLabel,
      methodLabel,

      // summary cards
      formatMoney,
      totalPaymentsCount,
      totalPaymentsAmount,
      pendingPaymentsAmount,
      failedPaymentsAmount,
      successRate,

      // chart
      chartOptions,
      chartSeries,
      selectedYearLabel,
      yearOptions,
      applyYearFilter,

      // filters + pagination
      handlePageChange,
      setSort,
      setMethod,

      // export
      handleExportPdf,
      handleExportExcel,
    };
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <!-- Page Wrapper -->
  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="handleExportPdf"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="handleExportExcel"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="row dq-stat-grid">
        <!-- Summary Cards (left side) -->
        <div class="col-xl-6 d-flex">
          <div class="row flex-fill">
            <!-- Total Payments (amount) -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex flex-wrap align-items-center justify-content-between border-bottom pb-2"
                  >
                    <div
                      class="d-flex align-items-center flex-column overflow-hidden"
                    >
                      <div>
                        <span class="fs-14 fw-normal text-truncate mb-1"
                          >Total Payments</span
                        >
                        <h5>KES {{ formatMoney(totalPaymentsAmount) }}</h5>
                      </div>
                    </div>
                    <div
                      class="d-flex justify-content-between align-items-center flex-wrap"
                    >
                      <a
                        href="javascript:void(0);"
                        class="avatar avatar-md br-5 payment-report-icon bg-transparent-primary border border-primary"
                      >
                        <span class="text-primary">
                          <i class="ti ti-currency-dollar"></i>
                        </span>
                      </a>
                    </div>
                  </div>
                  <div class="d-flex justify-content-center mt-2">
                    <p
                      class="fs-12 fw-normal d-flex align-items-center text-truncate"
                    >
                      <span
                        class="text-success fs-12 d-flex align-items-center me-1"
                      >
                        <i class="ti ti-arrow-wave-right-up me-1"></i>
                        <!-- you can wire a real % change later -->
                        +0.00%
                      </span>
                      from last period
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pending Payments (currently 0 until you track them) -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex flex-wrap align-items-center justify-content-between border-bottom pb-2"
                  >
                    <div
                      class="d-flex align-items-center flex-column overflow-hidden"
                    >
                      <div>
                        <span class="fs-14 fw-normal text-truncate mb-1"
                          >Pending Payments</span
                        >
                        <h5>KES {{ formatMoney(pendingPaymentsAmount) }}</h5>
                      </div>
                    </div>
                    <div
                      class="d-flex justify-content-between align-items-center flex-wrap"
                    >
                      <a
                        href="javascript:void(0);"
                        class="avatar avatar-md br-5 payment-report-icon bg-transparent-skyblue border border-skyblue"
                      >
                        <span class="text-skyblue">
                          <i class="ti ti-currency-dollar"></i>
                        </span>
                      </a>
                    </div>
                  </div>
                  <div class="d-flex justify-content-center mt-2">
                    <p
                      class="fs-12 fw-normal d-flex align-items-center text-truncate"
                    >
                      <span
                        class="text-success fs-12 d-flex align-items-center me-1"
                      >
                        <i class="ti ti-arrow-wave-right-up me-1"></i>
                        0.00%
                      </span>
                      from last period
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Failed Payments (currently 0) -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex flex-wrap align-items-center justify-content-between border-bottom pb-2"
                  >
                    <div
                      class="d-flex align-items-center flex-column overflow-hidden"
                    >
                      <div>
                        <span class="fs-14 fw-normal text-truncate mb-1"
                          >Failed Payments</span
                        >
                        <h5>KES {{ formatMoney(failedPaymentsAmount) }}</h5>
                      </div>
                    </div>
                    <div
                      class="d-flex justify-content-between align-items-center flex-wrap"
                    >
                      <a
                        href="javascript:void(0);"
                        class="avatar avatar-md br-5 payment-report-icon bg-transparent-danger border border-danger"
                      >
                        <span class="text-danger">
                          <i class="ti ti-currency-dollar"></i>
                        </span>
                      </a>
                    </div>
                  </div>
                  <div class="d-flex justify-content-center mt-2">
                    <p
                      class="fs-12 fw-normal d-flex align-items-center text-truncate"
                    >
                      <span
                        class="text-danger fs-12 d-flex align-items-center me-1"
                      >
                        <i class="ti ti-arrow-wave-right-up me-1"></i>
                        0.00%
                      </span>
                      from last period
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Success Rate -->
            <div class="col-lg-6 col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex flex-wrap align-items-center justify-content-between border-bottom pb-2"
                  >
                    <div
                      class="d-flex align-items-center flex-column overflow-hidden"
                    >
                      <div>
                        <span class="fs-14 fw-normal text-truncate mb-1"
                          >Payment Success Rate</span
                        >
                        <h5>{{ successRate }}%</h5>
                      </div>
                    </div>
                    <div
                      class="d-flex justify-content-between align-items-center flex-wrap"
                    >
                      <a
                        href="javascript:void(0);"
                        class="avatar avatar-md br-5 payment-report-icon bg-pink-transparent border border-pink"
                      >
                        <span class="text-pink">
                          <i class="ti ti-currency-dollar"></i>
                        </span>
                      </a>
                    </div>
                  </div>
                  <div class="d-flex justify-content-center mt-2">
                    <p
                      class="fs-12 fw-normal d-flex align-items-center text-truncate"
                    >
                      <span
                        class="text-success fs-12 d-flex align-items-center me-1"
                      >
                        <i class="ti ti-arrow-wave-right-up me-1"></i>
                        0.00%
                      </span>
                      from last period
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Donut chart: Payments by method -->
        <div class="col-xl-6 d-flex">
          <div class="card flex-fill">
            <div class="card-header border-0">
              <div
                class="d-flex flex-wrap row-gap-2 justify-content-between align-items-center"
              >
                <div class="d-flex align-items-center">
                  <span class="me-2">
                    <i class="ti ti-chart-donut text-danger"></i>
                  </span>
                  <h5>Payments By Payment Methods</h5>
                </div>
                <div class="dropdown">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-toggle btn btn-sm fs-12 btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {{ selectedYearLabel }}
                  </a>
                  <ul class="dropdown-menu dropdown-menu-end p-2">
                    <li v-for="opt in yearOptions" :key="opt.value">
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="applyYearFilter(opt)"
                      >
                        {{ opt.label }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div
              class="card-body d-flex align-items-center justify-content-between pt-0"
            >
              <div class="row align-items-center w-100">
                <div class="col-md-6">
                  <div class="position-relative payment-total">
                    <div id="payment-report">
                      <apexchart
                        type="donut"
                        height="190"
                        :options="chartOptions"
                        :series="chartSeries"
                      ></apexchart>
                    </div>
                    <div class="payment-total-content">
                      <span class="display-3 fs-24 fw-bold text-skyblue">
                        {{ totalPaymentsCount }}
                      </span>
                      <p class="fs-16 fw-normal">payments</p>
                    </div>
                  </div>
                </div>

                <!-- Simple legend by method (from chartOptions.labels) -->
                <div class="col-md-6">
                  <div class="row gy-3">
                    <div
                      class="col-md-6"
                      v-for="(label, idx) in chartOptions.labels || []"
                      :key="label"
                    >
                      <h6 class="fs-16 text-gray-5 fw-normal side-badge mb-1">
                        {{ label }}
                      </h6>
                      <h5 class="fs-20 fw-bold">
                        KES
                        {{
                          formatMoney((chartSeries && chartSeries[idx]) || 0)
                        }}
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment List -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Payment List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3 dq-list-toolbar"
          >
            <!-- Date range -->
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                  readonly
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>

            <!-- Payment Type filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Payment Type: {{ methodLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setMethod('', 'All Methods')"
                  >
                    All Methods
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setMethod('Cash', 'Cash')"
                  >
                    Cash
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setMethod('Bank Transfer', 'Bank Transfer')"
                  >
                    Bank Transfer
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setMethod('Card', 'Card')"
                  >
                    Card
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setMethod('Mobile Money', 'Mobile Money')"
                  >
                    Mobile Money
                  </a>
                </li>
                <!-- Add more as your data needs -->
              </ul>
            </div>

            <!-- Sort dropdown -->
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Sort By : {{ sortLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('recent', 'Recently Added')"
                    >Recently Added</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="
                      setSort('amount_asc', 'Amount (Low to High)')
                    "
                    >Amount (Low to High)</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="
                      setSort('amount_desc', 'Amount (High to Low)')
                    "
                    >Amount (High to Low)</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('oldest', 'Oldest First')"
                    >Oldest</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <payments-report-table
              :meta="meta"
              :loading="loading"
              @page-change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>
  <!-- /Page Wrapper -->
</template>

<style scoped>
@media (max-width: 767.98px) {
  .dq-list-toolbar {
    align-items: stretch !important;
  }

  .dq-list-toolbar > * {
    width: 100%;
    margin-right: 0 !important;
  }

  .dq-list-toolbar .dropdown,
  .dq-list-toolbar .dropdown-toggle,
  .dq-list-toolbar .form-control {
    width: 100%;
  }

  .dq-stat-card .card-body {
    padding: 1rem;
  }

  .payment-total-content {
    position: static !important;
    text-align: center;
    margin-top: 0.5rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>


